import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import { useUser } from "../../user/utils/user";

import { AgGridWithOverlay } from "../../common/components/AgGridWithOverlay";
import LoadingModal from "../../common/components/LoadingModal.js";
import Select from "../../common/components/form/Select.js";
import { useRoleClientNavigate } from "../../common/hooks/useRoleClientNavigate";
import { get } from "../../common/utils/api";

function ReviewForm({ groupOptions, countryOptions, t }) {
  return (
    <React.Fragment>
      <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
        <div className="space-y-6 sm:space-y-5">
          <Select
            label={"Select Group"}
            name={"formGroup"}
            options={groupOptions}
          />
          <Select
            label={"Select Country"}
            name={"formCountry"}
            options={countryOptions}
          />
        </div>
      </div>
      <div className="py-5">
        <div className="flex justify-end">
          <button
            type="submit"
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.viewAssignedReviews", "View assigned reviews")}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

function ReviewQueue({ showAlert, t }) {
  let [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);
  const [groupOptions, setGroupOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const navigate = useRoleClientNavigate();
  const { user } = useUser();

  // Use session storage for the existing user and dates if they exist
  const initialValues = {
    formGroup: sessionStorage.getItem("group") || "All",
    formCountry: sessionStorage.getItem("country") || "All",
  };

  let summaryColumnDefs = [
    {
      field: "review",
      headerName: "",
      width: 80,
      cellRenderer: () => t("plot.review", "review"),
      onCellClicked: function (params) {
        navigate(
          `/review/detail?user=${params.data.user}&date=${params.data.date}`,
        );
      },
      cellClass: ["text-cyan-600", "hover:text-cyan-900", "cursor-pointer"],
    },
    { field: "user", headerName: t("user.user", "User") },
    { field: "count", headerName: t("user.numberRemaining", "# Remaining") },
    {
      field: "date",
      headerName: t("common.createddate", "Date"),
      width: 120,
    },
  ];

  const getGroups = async () => {
    get("/users/groups/?new_review=true").then((response) => {
      setGroupOptions(["All", ...response.data]);
    });
  };

  const getCountries = async () => {
    get("/users/countries/").then((response) => {
      setCountryOptions(["All", ...response.data.map((c) => c.name)]);
    });
  };

  const getData = async (group_selected, country_selected, setSubmitting) => {
    get(
      `/data/review/queue?group=${group_selected}&country=${country_selected}`,
    )
      .then((response) => response.data)
      .then((data) => {
        // Sort the summaryData array by date in descending order
        data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSummaryData(data);
      })
      .catch((error) => {
        console.error(error);
        setError(error);
      })
      .finally(() => setTimeout(() => setSubmitting(false), 300));
  };

  const grid = useRef();
  const onGridReady = (params) => {
    grid.current = params.api;
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    getGroups();
    getCountries();
  }, []);

  function onSubmit(value, { setSubmitting }) {
    getData(value.formGroup, value.formCountry, setSubmitting);
    sessionStorage.setItem("group", value.formGroup);
    sessionStorage.setItem("country", value.formCountry);
  }

  return (
    <React.Fragment>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 bg-white border-gray-200 shadow rounded-md pt-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.summary", "Generate Review Queue")}
            </h3>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <React.Fragment>
                <LoadingModal open={isSubmitting} />
                <Form className="space-y-8 divide-y divide-gray-200">
                  <ReviewForm
                    user={user}
                    groupOptions={groupOptions}
                    countryOptions={countryOptions}
                    t={t}
                  />
                </Form>
              </React.Fragment>
            )}
          </Formik>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Review queue")}
            </h3>
          </div>
        </div>
        <div className="ag-theme-alpine mt-4" style={{ height: 400 }}>
          <AgGridWithOverlay
            ref={grid}
            rowData={summaryData}
            columnDefs={summaryColumnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            onGridReady={onGridReady}
            error={error}
            t={t}
            instructionKey="common.clickViewAssignedReviews"
            instructionDefault="Click on View assigned reviews to see the data"
          />
        </div>
      </div>
    </React.Fragment>
  );
}
export default withTranslation()(ReviewQueue);
